import { useCallback, useMemo, useRef } from 'react';
import { DateTime } from 'luxon';

import { TimeValidationError } from '@mui/x-date-pickers';

import {
    dateTimeToPossibleISO8601Time,
    DEFAULT_TIME_ZONE,
    INVALID_DATE_TIME_VALUE,
    PossibleISO8601,
} from 'app/core/date-time';
import { IANATimeZone } from 'app/core/types';

import { makeDateTimeChangeEvent, nullifyDate } from './transformers';
import { TimeChangeEvent } from './types';

interface TimeFieldValueProps {
    name?: string;
    onChange?: (event: TimeChangeEvent) => void;
    timeZone?: IANATimeZone;
    value: PossibleISO8601 | undefined;
}

export function useTimeFieldValue(props: TimeFieldValueProps) {
    const { name, onChange, timeZone = DEFAULT_TIME_ZONE, value } = props;
    const latestDateTimeRef = useRef<DateTime | null>(null);

    const handleChange = useCallback(
        (dateTime: DateTime | null, { validationError }: { validationError?: TimeValidationError | null }) => {
            latestDateTimeRef.current = dateTime;

            const value = dateTimeToPossibleISO8601Time(dateTime);

            onChange?.(
                makeDateTimeChangeEvent({
                    name,
                    timeZone,
                    validationError,
                    value,
                }),
            );
        },
        [onChange, name, timeZone],
    );

    const dateTime = useMemo(() => {
        if (value === INVALID_DATE_TIME_VALUE) {
            return latestDateTimeRef.current ?? null;
        }

        return value ? nullifyDate(value, timeZone) : null;
    }, [value, timeZone]);

    return {
        dateTime,
        handleChange,
    };
}
